import { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import {
  Button,
  Card,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '@centraoneplus/ui';
import { useCreateCapability, useUpdateCapability } from '@hooks/useIntegrations';
import type { Capability, CapabilityParameter } from '@services/integrations.service';

interface CapabilityEditorProps {
  capability: Capability | null;
  onClose: () => void;
}

interface ParameterForm {
  id: string;
  name: string;
  type: string;
  description: string;
  required: boolean;
  default?: string;
  enum?: string;
}

const PARAMETER_TYPES = [
  { value: 'string', label: 'String' },
  { value: 'integer', label: 'Integer' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
];

const CATEGORIES = [
  { value: 'ticket', label: 'Ticket' },
  { value: 'customer', label: 'Customer' },
  { value: 'order', label: 'Order' },
  { value: 'account', label: 'Account' },
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'custom', label: 'Custom' },
];

export function CapabilityEditor({ capability, onClose }: CapabilityEditorProps) {
  const toast = useToast();
  const isEditing = !!capability;

  // Form state
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('custom');
  const [connectorId, setConnectorId] = useState('');
  const [connectorAction, setConnectorAction] = useState('');
  const [tags, setTags] = useState('');
  const [rateLimit, setRateLimit] = useState('100');
  const [parameters, setParameters] = useState<ParameterForm[]>([]);

  // Mutations
  const createCapability = useCreateCapability();
  const updateCapability = useUpdateCapability();

  const isLoading = createCapability.isPending || updateCapability.isPending;

  // Initialize form with existing capability
  useEffect(() => {
    if (capability) {
      setId(capability.id);
      setName(capability.name);
      setDescription(capability.description);
      setCategory(capability.category);
      setConnectorId(capability.connector_id || '');
      setConnectorAction(capability.connector_action || '');
      setTags(capability.tags.join(', '));
      setParameters(
        capability.parameters.map((p, i) => ({
          id: `param-${i}`,
          name: p.name,
          type: p.type,
          description: p.description,
          required: p.required,
          default: p.default?.toString() || '',
          enum: p.enum?.join(', ') || '',
        }))
      );
    }
  }, [capability]);

  const addParameter = () => {
    setParameters([
      ...parameters,
      {
        id: `param-${Date.now()}`,
        name: '',
        type: 'string',
        description: '',
        required: false,
      },
    ]);
  };

  const removeParameter = (id: string) => {
    setParameters(parameters.filter((p) => p.id !== id));
  };

  const updateParameter = (id: string, field: keyof ParameterForm, value: unknown) => {
    setParameters(
      parameters.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      )
    );
  };

  const handleSubmit = async () => {
    // Validate
    if (!id.trim()) {
      toast.error('Validation Error', 'ID is required');
      return;
    }
    if (!name.trim()) {
      toast.error('Validation Error', 'Name is required');
      return;
    }
    if (!description.trim()) {
      toast.error('Validation Error', 'Description is required');
      return;
    }

    // Build parameters
    const formattedParams = parameters
      .filter((p) => p.name.trim())
      .map((p) => ({
        name: p.name,
        type: p.type as CapabilityParameter['type'],
        description: p.description,
        required: p.required,
        default: p.default || undefined,
        enum: p.enum ? p.enum.split(',').map((e) => e.trim()).filter(Boolean) : undefined,
      }));

    try {
      if (isEditing) {
        await updateCapability.mutateAsync({
          capabilityId: capability.id,
          data: {
            name,
            description,
            parameters: formattedParams,
            connector_config: connectorAction ? { action: connectorAction } : undefined,
            tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
            rate_limit: parseInt(rateLimit, 10) || 100,
          },
        });
        toast.success('Capability updated', `${name} has been updated`);
      } else {
        await createCapability.mutateAsync({
          id,
          name,
          description,
          category,
          parameters: formattedParams,
          connector_id: connectorId || undefined,
          connector_action: connectorAction || undefined,
          tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
          rate_limit: parseInt(rateLimit, 10) || 100,
        });
        toast.success('Capability created', `${name} has been created`);
      }
      onClose();
    } catch (err) {
      toast.error(
        isEditing ? 'Update failed' : 'Create failed',
        err instanceof Error ? err.message : 'Unknown error'
      );
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEditing ? 'Edit Capability' : 'Create Capability'}
      size="lg"
    >
      <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Basic Info */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Basic Information</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                ID *
              </label>
              <Input
                placeholder="e.g., get_order_status"
                value={id}
                onChange={(e) => setId(e.target.value)}
                disabled={isEditing}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Category
              </label>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={isEditing}
                options={CATEGORIES}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
              Name *
            </label>
            <Input
              placeholder="e.g., Get Order Status"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
              Description *
            </label>
            <Textarea
              placeholder="Describe what this capability does..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        {/* Parameters */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Parameters</h3>
            <Button variant="outline" size="sm" onClick={addParameter}>
              <Plus className="h-4 w-4 mr-1" />
              Add Parameter
            </Button>
          </div>

          {parameters.length === 0 ? (
            <Card className="p-4 text-center text-gray-500 text-sm dark:text-gray-400">
              No parameters defined. Click "Add Parameter" to add one.
            </Card>
          ) : (
            <div className="space-y-3">
              {parameters.map((param) => (
                <Card key={param.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <GripVertical className="h-5 w-5 text-gray-400 mt-2 dark:text-gray-500" />

                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <Input
                          placeholder="Parameter name"
                          value={param.name}
                          onChange={(e) =>
                            updateParameter(param.id, 'name', e.target.value)
                          }
                        />
                        <Select
                          value={param.type}
                          onChange={(e) =>
                            updateParameter(param.id, 'type', e.target.value)
                          }
                          options={PARAMETER_TYPES}
                        />
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={param.required}
                              onChange={(e) =>
                                updateParameter(
                                  param.id,
                                  'required',
                                  e.target.checked
                                )
                              }
                            />
                            Required
                          </label>
                        </div>
                      </div>

                      <Input
                        placeholder="Description"
                        value={param.description}
                        onChange={(e) =>
                          updateParameter(param.id, 'description', e.target.value)
                        }
                      />

                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          placeholder="Default value"
                          value={param.default || ''}
                          onChange={(e) =>
                            updateParameter(param.id, 'default', e.target.value)
                          }
                        />
                        <Input
                          placeholder="Enum values (comma-separated)"
                          value={param.enum || ''}
                          onChange={(e) =>
                            updateParameter(param.id, 'enum', e.target.value)
                          }
                        />
                      </div>
                    </div>

                    <button
                      onClick={() => removeParameter(param.id)}
                      className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Connector Configuration */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Connector Configuration</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Connector ID
              </label>
              <Input
                placeholder="e.g., shopify"
                value={connectorId}
                onChange={(e) => setConnectorId(e.target.value)}
                disabled={isEditing}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Connector Action
              </label>
              <Input
                placeholder="e.g., GET /orders/{id}"
                value={connectorAction}
                onChange={(e) => setConnectorAction(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Metadata</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Tags
              </label>
              <Input
                placeholder="e.g., orders, shipping, tracking"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Comma-separated</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Rate Limit
              </label>
              <Input
                type="number"
                placeholder="100"
                value={rateLimit}
                onChange={(e) => setRateLimit(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Requests per minute</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 dark:bg-gray-900">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {isEditing ? 'Updating...' : 'Creating...'}
            </>
          ) : isEditing ? (
            'Update Capability'
          ) : (
            'Create Capability'
          )}
        </Button>
      </div>
    </Modal>
  );
}
